/**
 * Shared building blocks for the output schema's configurable fields, plus the
 * compact `OutputConfigFields` layout shown when the full Output section is
 * hidden but a task schema still has an output schema (e.g. flowchart
 * sidepanel). `OutputConfigSection` composes the same building blocks in its
 * own layout so behavior stays consistent across both renderings.
 */
import {Box} from '@mantine/core';
import React from 'react';

import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';

import ConfigureTaskFinderSection from './ConfigureTaskFinderSection';
import AutopopulateFromTasksField from './fields/AutopopulateFromTasksField';
import {PrefixField} from './ProcessSchemaMetadataSectionRow';
import {EXECUTION_TYPES_WITH_TASK_FINDER} from './ProcessTaskSchemaEditorConstants';
import type {ProcessTaskSchema} from './ProcessTaskSchemaUtils';
import type {ProcessTaskSchemaEditorStateResult} from './useProcessTaskSchemaEditorState';

/**
 * Builds the props for the output schema's `PrefixField`.
 */
export function getOutputPrefixFieldProps(
  state: ProcessTaskSchemaEditorStateResult,
  label: string
): React.ComponentProps<typeof PrefixField> {
  return {
    label,
    value: state.outputSchemaPrefix,
    error: state.errors.outputSchemaPrefix ?? '',
    isBeingCreated: state.isBeingCreated,
    isDisabled: state.isArchived || state.isEditingFlowchart || state.isLocked,
    onChange: async (updatedOutputSchemaPrefix: string) => {
      if (updatedOutputSchemaPrefix === state.outputSchemaPrefix) {
        return;
      }
      if (!state.isBeingCreated) {
        await state.updateProcessTaskSchemaWithFields({
          outputSchemaPrefix: updatedOutputSchemaPrefix,
        });
      } else {
        state.setOutputSchemaPrefix(updatedOutputSchemaPrefix);
      }
    },
    prefixPreview: state.generatePrefixPreview('Output ID preview', state.outputSchemaPrefix),
    dataTestAttrs: {component: 'ProcessTaskSchemaEditor', element: 'outputPrefixField'},
  };
}

interface OutputAutopopulateFieldProps {
  state: ProcessTaskSchemaEditorStateResult;
}

export function OutputAutopopulateField(props: OutputAutopopulateFieldProps): JSX.Element {
  const {state} = props;
  return (
    <AutopopulateFromTasksField
      value={state.autopopulateDirectOutputsFromTasks}
      disabled={state.isLocked}
      onChange={async (value) => {
        if (value === state.autopopulateDirectOutputsFromTasks) {
          return;
        }
        if (!state.isBeingCreated) {
          await state.updateProcessTaskSchemaWithFields({
            autopopulateDirectOutputsFromTasks: value,
          });
        } else {
          state.setAutopopulateDirectOutputsFromTasks(value);
        }
      }}
    />
  );
}

interface OutputTaskFinderSectionProps {
  state: ProcessTaskSchemaEditorStateResult;
  processTaskSchema: ProcessTaskSchema | null;
}

export function OutputTaskFinderSection(props: OutputTaskFinderSectionProps): JSX.Element | null {
  const {state, processTaskSchema} = props;
  if (!state.isBeingCreated && (!processTaskSchema || !processTaskSchema.outputSchema)) {
    return null;
  }
  return (
    <ConfigureTaskFinderSection
      isBeingCreated={state.isBeingCreated}
      isLoading={!state.haveSchemasLoaded}
      taskFinderLookupConfig={state.taskFinderLookupConfig}
      outputSchema={processTaskSchema?.outputSchema ?? null}
      taskSchema={processTaskSchema ?? null}
      isArchived={state.isArchived}
      error={state.errors.taskFinderLookupConfig}
      onChange={async (updatedLookupConfig) => {
        await state.updateProcessTaskSchemaWithFields({
          taskFinderLookupConfig: updatedLookupConfig,
          updateTaskFinder: true,
        });
      }}
      locked={state.isLocked}
    />
  );
}

interface OutputConfigFieldsProps {
  state: ProcessTaskSchemaEditorStateResult;
  processTaskSchema: ProcessTaskSchema | null;
}

export default function OutputConfigFields(props: OutputConfigFieldsProps): JSX.Element {
  const {state, processTaskSchema} = props;

  return (
    <>
      <PrefixField {...getOutputPrefixFieldProps(state, 'Output ID prefix')} />
      {state.executionType === ProcessExecutionType.DIRECT && (
        <Box mt="xl">
          <OutputAutopopulateField state={state} />
        </Box>
      )}
      {EXECUTION_TYPES_WITH_TASK_FINDER.has(state.executionType) && (
        <OutputTaskFinderSection state={state} processTaskSchema={processTaskSchema} />
      )}
    </>
  );
}
